import { Kbd } from '../../ui/primitives.tsx'
import styles from './help.module.css'

/**
 * Instructions and shortcuts (asked for after the first real session — none of this was written
 * down anywhere).
 *
 * **Every shortcut listed here was checked against the code that implements it**, not written from
 * memory. A help page that lies is worse than no help page: it teaches something that does not
 * work and makes the person doubt the app rather than the documentation.
 *
 * If you add a shortcut, add it here. If you remove one, remove it here first.
 */

import { MODIFIER } from '../../lib/platform.ts'

interface Shortcut {
  keys: string[]
  what: string
  where: string
  /** Domyślnie „+" (naciśnij razem). „lub" dla klawiszy, z których wybiera się jeden. */
  joiner?: 'plus' | 'or'
}

const GLOBAL: Shortcut[] = [
  { keys: ['n'], what: 'Nowe zadanie w pierwszej kolumnie', where: 'Board' },
  { keys: ['/'], what: 'Kursor do pola wyszukiwania', where: 'Board, Tabela' },
  { keys: ['Esc'], what: 'Zamknij drawer, menu albo szybkie dodawanie', where: 'Wszędzie' },
]

const IN_TASK: Shortcut[] = [
  { keys: ['1'], what: 'Priorytet wysoki', where: 'Otwarte zadanie' },
  { keys: ['2'], what: 'Priorytet normalny', where: 'Otwarte zadanie' },
  { keys: ['3'], what: 'Priorytet niski', where: 'Otwarte zadanie' },
  { keys: ['e'], what: 'Do archiwum (z potwierdzeniem)', where: 'Otwarte zadanie' },
]

const TYPING: Shortcut[] = [
  { keys: [MODIFIER, 'Enter'], what: 'Wyślij komentarz', where: 'Pole komentarza' },
  { keys: ['Enter'], what: 'Zatwierdź i wyjdź z pola', where: 'Tytuł zadania, tytuł notatki' },
  { keys: ['@'], what: 'Lista osób do oznaczenia', where: 'Komentarz, opis, notatka' },
  { keys: ['↑', '↓'], what: 'Wybór osoby z listy', where: 'Otwarta lista', joiner: 'or' },
  { keys: ['Tab'], what: 'Wstaw wybraną osobę', where: 'Otwarta lista' },
]

export function HelpView(): React.JSX.Element {
  return (
    <div className={styles.wrap}>
      <h1 className={styles.title}>Pomoc</h1>

      <section className={styles.panel}>
        <h2 className="section-label">Co gdzie jest</h2>
        <dl className={styles.defs}>
          <dt>Start</dt>
          <dd>
            Ekran na wejście: co jest po terminie, co masz na sobie, kto cię oznaczył i co ostatnio
            poszło do przodu. Kliknięcie w dowolną pozycję otwiera zadanie.
          </dd>

          <dt>Board</dt>
          <dd>
            Kanban. Karty przeciąga się między kolumnami i wewnątrz nich; kolejność zapisuje się
            sama. Kliknięcie karty otwiera szczegóły z boku.
          </dd>

          <dt>Tabela</dt>
          <dd>
            Te same zadania w innym układzie. Sortowanie przez kliknięcie nagłówka, status i
            priorytet zmienia się w miejscu. Szerokość kolumn ustawiasz przeciągając krawędź
            nagłówka — zostaje zapamiętana. Podwójny klik w krawędź wraca do domyślnej.
          </dd>

          <dt>Zamknięte</dt>
          <dd>
            Zadania z kolumny kończącej przepływ (domyślnie „Done") oraz archiwum. Trafiają tu same,
            gdy przesuniesz je do tej kolumny — dzięki temu Tabela pokazuje wyłącznie pracę w toku.
          </dd>

          <dt>Notatki</dt>
          <dd>
            Wspólne dokumenty. Piszecie jednocześnie, zmiany scalają się same. Nie ma przycisku
            „Zapisz" i nie ma konfliktów — jedyny wskaźnik to „zapisano" przy tytule.
          </dd>

          <dt>Ustawienia</dt>
          <dd>
            Nazwa wyświetlana, motyw oraz tokeny dla agentów AI. Wartość tokena widać jeden raz, w
            bazie jest tylko jego skrót.
          </dd>
        </dl>
      </section>

      <section className={styles.panel}>
        <h2 className="section-label">Wzmianki</h2>
        <p className={styles.text}>
          Wpisz <code className={styles.code}>@</code> w komentarzu, w opisie zadania albo w notatce
          — pojawi się lista osób. Strzałkami wybierasz, <Kbd>Enter</Kbd> albo <Kbd>Tab</Kbd>{' '}
          wstawia, <Kbd>Esc</Kbd> zamyka. Oznaczona osoba zobaczy licznik przy dzwonku w górnym
          pasku, od razu, bez odświeżania. Kliknięcie w powiadomienie otwiera to miejsce i gasi
          licznik.
        </p>
        <p className={styles.text}>
          Rozpoznawana jest nazwa wyświetlana bez spacji albo część e-maila przed małpą. Wielkość
          liter nie ma znaczenia. Nierozpoznane <code className={styles.code}>@coś</code> zostaje
          zwykłym tekstem i <strong>nie podświetla się</strong> — po kolorze poznasz, że wzmianka
          faktycznie kogoś znalazła.
        </p>
        <p className={styles.hint}>
          Oznaczyć kogoś może też agent AI przez MCP. Powiadomienie wygląda i działa tak samo.
        </p>
      </section>

      <section className={styles.panel}>
        <h2 className="section-label">Skróty klawiszowe</h2>
        <ShortcutTable rows={GLOBAL} />
        <ShortcutTable rows={IN_TASK} />
        <ShortcutTable rows={TYPING} />
        <p className={styles.hint}>
          Skróty nie działają, kiedy kursor stoi w polu tekstowym — inaczej litera „e" w tytule
          archiwizowałaby zadanie.
        </p>
      </section>

      <section className={styles.panel}>
        <h2 className="section-label">Agent AI</h2>
        <p className={styles.text}>
          Token wygenerujesz w Ustawieniach, tam też jest gotowa komenda do wklejenia. Agent robi to
          samo co człowiek: zakłada zadania, komentuje, edytuje notatki. Każda jego zmiana zapisuje
          się na konto właściciela tokena i pojawia się u wszystkich na żywo — z podświetleniem,
          żeby było widać, że to nie ty.
        </p>
      </section>
    </div>
  )
}

function ShortcutTable({ rows }: { rows: Shortcut[] }): React.JSX.Element {
  return (
    <div className={styles.keys}>
      {rows.map((row) => (
        <div key={row.keys.join('+') + row.what} className={styles.keyRow}>
          <span className={styles.keyCombo}>
            {row.keys.map((key, index) => (
              <span key={key}>
                {index > 0 && (
                  <span className={styles.plus}>{row.joiner === 'or' ? 'lub' : '+'}</span>
                )}
                <Kbd>{key}</Kbd>
              </span>
            ))}
          </span>
          <span className={styles.keyWhat}>{row.what}</span>
          <span className={styles.keyWhere}>{row.where}</span>
        </div>
      ))}
    </div>
  )
}
